import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { req } from './ServerUtils';
import { makeDesign } from './utils';
import { useUser } from './UserProvider';
import HueSlider from './HueSlider';
import { IconButton } from '@mui/material';
import CropPortraitIcon from '@mui/icons-material/CropPortrait';
import CropLandscapeIcon from '@mui/icons-material/CropLandscape';
import { shiftColorsToHue } from './Colors';


const presets = [{
    name: 'Portrait',
    width: 600,
    height: 1200,
    icon: <CropPortraitIcon />
  }, {
    name: 'Landscape',
    width: 800,
    height: 1200,
    icon: <CropLandscapeIcon />
  }]

const downloadCode = `
<script>
    function screenshot() {
        domtoimage.toPng(document.body).then((dataUrl) => {
            var link = document.createElement('a');
            link.download = 'makemybrand-infographic.png';
            link.href = dataUrl;
            link.click();
        }).catch(e => {
            console.error(e);
        });
    }
    document.addEventListener('DOMContentLoaded', async function screenshotWrapper() {
        await screenshot();
        document.removeEventListener('DOMContentLoaded', screenshotWrapper);
    });
</script>
`;

const code = `<script src="https://mmb-js.s3.us-east-1.amazonaws.com/dom-to-image-new.js"></script>
<style>
.mmb-image-background, .mmb-image {
    cursor: pointer;
}
</style>
<script>
document.addEventListener('DOMContentLoaded', async function imageClickWrapper() {
    document.removeEventListener('DOMContentLoaded', imageClickWrapper);

    function addOnClick(selector, type) {
        const els = document.querySelectorAll(selector);
        for (const el of els) {
            el.addEventListener('click', (e) => {
                const elem = e.target;
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.style.display = 'none';
                input.onchange = e => { 
                    const file = e.target.files[0];
                    const reader = new FileReader();
                    
                    reader.onload = (readerEvent) => {
                        const content = readerEvent.target.result; 
                        if (type == 'image-background') {
                            elem.setAttribute('style', 'background-image: url(' + content + ')');
                        } else {
                            elem.setAttribute('src', content);
                        }
                    }
                    reader.readAsDataURL(file);
                    document.body.removeChild(input);
                }
                document.body.appendChild(input);
                input.click();

            });
        }
    }
    addOnClick('.mmb-image-background', 'image-background');
    addOnClick('.mmb-image', 'image');

    const editableClasses = ['.mmb-title', '.mmb-subtitle', '.mmb-body'];
    for (const editableClass of editableClasses) {
        const elems = document.querySelectorAll(editableClass);
        for (const elem of elems) {
            elem.setAttribute('contenteditable', 'true');
        
            elem.onclick = function(e) {
                e.stopPropagation();
                e.preventDefault();
            }
        }

    }
});
</script>`;


const DesignEditor = ({id}) => {
    const {userInfo} = useUser();

    const [isTablet, setIsTablet] = useState(window.matchMedia('(max-width: 600px)').matches);

    const { urlId } = useParams();
    const designId = urlId || id;
    const iframeRef = useRef(null);
    const [config, setConfig] = useState({});
    const [design, setDesign] = useState(null);
    const [preset, setPreset] = useState(presets[0]);
    let [hue, setHue] = useState(0);
    
    const download = async () => {
        let htmlContent = iframeRef.current.contentDocument.documentElement.outerHTML;
        const originalHtml = htmlContent;
        htmlContent = htmlContent.replace('</head>', downloadCode + '</head>');

        const iframeDoc = iframeRef.current.contentDocument;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();

        setTimeout(() => {
            iframeDoc.open();
            iframeDoc.write(originalHtml);
            iframeDoc.close();
        }, 2000);
    };
    
    const handleToggle = (className) => {
        setConfig(prev => {
            const updatedConfig = {
                ...prev,
                [className]: { ...prev[className], visible: !prev[className].visible }
            };
            injectCSS(updatedConfig);
            return updatedConfig;
        });
    };

    const handlePropertyChange = (className, property, value) => {
        setConfig(prev => {
            const updatedConfig = {
                ...prev,
                [className]: { ...prev[className], [property]: value }
            };
            injectCSS(updatedConfig);
            return updatedConfig;
        });
    };
    useEffect(() => {
        // simply refresh so that if there's javascript in the iframe, it gets reloaded
        const iframeDoc = iframeRef.current?.contentDocument;
        if (!iframeDoc || !iframeDoc.documentElement) return;
        const html = iframeDoc.documentElement.outerHTML;
        iframeDoc.open();
        iframeDoc.write(html);
        iframeDoc.close();
    }, [preset]);


    useEffect(() => {
        console.log("shifting to ", hue)
        shiftColorsToHue(iframeRef.current.contentDocument.documentElement.outerHTML, hue).then(newHtml => {
            const iframeDoc = iframeRef.current.contentDocument;
            iframeDoc.open();
            iframeDoc.write(newHtml);
            iframeDoc.close();
        });
    }, [hue])

    const injectCSS = (updatedConfig) => {
        const iframeDoc = iframeRef.current.contentDocument;
        let styleTag = iframeDoc.getElementById('dynamic-styles');
        if (!styleTag) {
            styleTag = iframeDoc.createElement('style');
            styleTag.id = 'dynamic-styles';
            iframeDoc.head.appendChild(styleTag);
        }
        const css = Object.entries(updatedConfig)
            .map(([className, props]) => {
                const visibilityCSS = props.visible !== undefined
                    ? `display: ${props.visible ? 'block' : 'none'};`
                    : '';
                const dynamicStyles = Object.entries(props)
                    // .filter(([key]) => key !== 'visible')
                    .map(([key, value]) => `${key}: ${value};`)
                    .join(' ');
                return `${className} { ${visibilityCSS} ${dynamicStyles} }`;
            })
            .join(' ');
        styleTag.innerHTML = css;
    };

    useEffect(() => {
        const loadHTML = async () => {
            req(`/html_design?id=${designId}`).then((design) => {
                setDesign(design);
            });
        };
        loadHTML();
    }, [userInfo, designId]);

    useEffect(() => {
        if (!design || !design.pages[0].template_html) return;


        let html = makeDesign(design.pages[0].template_html, design.pages[0].data);
        html = html.replace('</head>', code + '</head>');

        const iframeDoc = iframeRef.current.contentDocument;
        iframeDoc.open();
        iframeDoc.write(html);
        iframeDoc.close();
    }, [design])

    return (
        <div>
            
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 20,
                padding: 20
            }}>
                
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    gap: 8, 
                    width: preset.width,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                    <div>Click on an image or text to edit it.</div>
                    <div style={{ 
                        display: 'flex', 
                        gap: 0, 
                        textAlign: "center",
                        justifyContent: 'start',
                    }}>
                        {presets.map((p, index) => (
                            <IconButton
                                key={index}
                                onClick={() => setPreset(p)}
                                variant="outlined"
                                color={preset.name === p.name ? 'primary' : 'default'}
                            >
                                {p.icon}
                            </IconButton>
                        ))}
                    </div>
                    
                    {/* <HueSlider hue={hue} setHue={(h) => {
                        hue = h;
                        setHue(h);
                    }} /> */}
                </div>
            
                <iframe
                    ref={iframeRef}
                    style={{ 
                        width: preset.width,
                        
                        border: '1px solid #ccc',
                        minHeight: isTablet ? "80vh" : "100vh",
                    }}
                />
                
            </div>

        </div>
    );
};



export default DesignEditor;


